import { useContext, useEffect, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { intlFormat } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

import { GeoContext } from "@context/GeoProvider";

import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { cn } from "@helpers/general";
import { isBrowser } from "@helpers/browser";

import useDeviceDetect from "@hooks/useDeviceDetect";
import { useLocalizedSentenceDict } from "@hooks/useSentenceDict";

import { ComponentType } from "@constants/ComponentType";

import { getShopifyStoreByCountry } from "../../../locale-shopifies";

import { ALPaymentMethod, ALLink as Link } from "@components/ALComponents";
import LocalesPopUp from "@components/locales/LocalesPopUp";
import CaptureEmail from "@components/CaptureEmail/captureEmail";
import FooterItem from "@components/Footer/FooterItem";

import InstagramIcon from "@svgs/instagram.svg";
import TikTokIcon from "@svgs/tiktok.svg";
import IconArrow from "@svgs/arrow.svg";
import MessageIcon from "@svgs/message.svg";
import LiveChatIcon from "@svgs/live-chat.svg";
import LuxeChatSupport from "@svgs/luxe-chat.svg";
import CallIcon from "@svgs/call.svg";

function Footer() {
  const { gePriceDetails } = useContext(GeoContext);
  const [isSmsTermsOpen, setIsSmsTermsOpen] = useState(false);
  const dict = useLocalizedSentenceDict();
  const { isMobile } = useDeviceDetect();

  const { knowledgeBase } = getShopifyStoreByCountry(gePriceDetails.CountryCode);
  const { customerSupport } = getShopifyStoreByCountry(gePriceDetails.CountryCode);

  const [hoursCustomerSupport, updateHoursCustomerSupport] = useState({});

  const { allContentfulFooter } = useStaticQuery(graphql`
    query SiteFooter {
      allContentfulFooter {
        nodes {
          node_locale
          footeritem {
            title
            link {
              ... on ContentfulCollapsibleLink {
                title
                links: link {
                  title
                  link {
                    link
                  }
                }
              }
              ... on ContentfulLink {
                title
                link {
                  link
                }
              }
            }
          }
        }
      }
    }
  `);

  let contentfulFooter = null;
  if (gePriceDetails) {
    contentfulFooter = allContentfulFooter.nodes.find(
      (x) => x.node_locale === gePriceDetails?.Culture
    );
    if (typeof contentfulFooter === "undefined") {
      contentfulFooter = allContentfulFooter.nodes.find((x) => x.node_locale === "en-US");
    }
  } else {
    contentfulFooter = allContentfulFooter.nodes.find((x) => x.node_locale === "en-US");
  }

  const sitemapElements = [
    {
      link: "/pages/sitemap",
      title: "Sitemap",
    },
    {
      link: "/policies/privacy-policy",
      title: "Privacy Policy",
    },
    {
      link: "/policies/terms-of-service",
      title: "T&C",
    },
    {
      link: "/pages/promo-sale",
      title: "Promo T&C",
    },
    {
      link: "/pages/sms-terms",
      title: "Sms T&C",
    },
    {
      link: "/pages/luxe-program-terms/",
      title: "AL Luxe T&C",
    },
    {
      link: "/pages/sms-privacy",
      title: "Sms Privacy Policy",
    },
    {
      link: "/pages/ca-transparency-in-supply-chains-act",
      title: "CA Supply Chains Act",
    },
  ];
  const socialMediaElements = [
    {
      link: "https://www.instagram.com/analuisany",
      title: "Instagram",
      image: <InstagramIcon width="22" fill="none" stroke="#000" />,
    },
    {
      link: "https://www.tiktok.com/@analuisany",
      title: "TikTok",
      image: <TikTokIcon width="22" fill="none" stroke="#000" />,
    },
  ];

  const getCurrentYear = () => new Date().getFullYear();

  useEffect(() => {
    if (customerSupport && isBrowser) {
      // convert hours for correct timezone
      const timeZone = gePriceDetails?.Timezone;

      const startParsedDate = new Date(customerSupport?.startHours);
      const startDate = utcToZonedTime(startParsedDate, timeZone);
      const startHourStr = intlFormat(
        startDate,
        { hour: "numeric" },
        { locale: gePriceDetails?.Culture }
      );

      const endParsedDate = new Date(customerSupport?.endHours);
      const endDate = utcToZonedTime(endParsedDate, timeZone);
      const endHourStr = intlFormat(
        endDate,
        { hour: "numeric" },
        { locale: gePriceDetails?.Culture }
      );
      updateHoursCustomerSupport({
        start: startHourStr.replace(" ", ""),
        end: endHourStr.replace(" ", ""),
      });
    }
  }, [customerSupport, gePriceDetails?.Culture, gePriceDetails.Timezone]);

  const toggleFooterLinks = (e) => {
    e.preventDefault();
    e.currentTarget.classList.toggle("active");
  };

  const openGorgias = () => {
    const hasGorgias = typeof window.GorgiasChat !== "undefined";
    if (hasGorgias) {
      window.GorgiasChat.open();
    }
  };

  return (
    <footer className="footer border-t border-dark-grey mobile:border-0">
      <div className="flex flex-row justify-between w-full px-[1.74vw] pt-[40px] pb-[50px] mx-auto mobile:flex-col-reverse mobile:px-0 mobile:pt-0 mobile:pb-0">
        <ul className="flex gap-[80px] mobile:flex-wrap mobile:flex-row mobile:gap-0 mobile:pt-[30px] mobile:pb-0">
          {contentfulFooter?.footeritem.map((el, i) => (
            <li
              key={`footer_item_${el.title}`}
              className={cn(
                "flex flex-col mobile:w-1/2",
                i % 2 === 0 ? "mobile:pl-[15px] mobile:pr-[15px]" : "mobile:pr-[15px]",
                "mobile:border-b mobile:border-light-grey mobile:pb-[30px] mobile:mb-[30px]"
              )}
            >
              {el.link[0].link.link ? (
                <a
                  href={el.link[0].link.link}
                  className="flex items-center w-full whitespace-nowrap mb-[20px] typo-body font-bold"
                  onClick={isMobile && i < 2 ? (e) => toggleFooterLinks(e) : null}
                  disabled={!isMobile || (isMobile && i >= 2)}
                  type="button"
                >
                  {el.title}
                  {/* {isMobile && i < 2 && <IconArrow style={{ transform: "rotate(270deg)" }} className="ml-[10px]" width="7" height="14" />} */}
                </a>
              ) : (
                <p className="flex items-center w-full whitespace-nowrap typo-body font-bold mb-[20px]">
                  {el.title}
                  {/* {isMobile && i < 2 && <IconArrow style={{ transform: "rotate(270deg)" }} className="ml-[10px]" width="7" height="14" />} */}
                </p>
              )}
              <ul className="flex flex-col list-none pl-0 gap-[15px] mobile:gap-[18px]">
                {el.link.map((footerLink) => {
                  let _link = footerLink?.link?.link;
                  if (_link === "https://help.analuisa.com") {
                    _link = knowledgeBase;
                  }
                  return (
                    <FooterItem
                      key={`footer_item_${footerLink.title}_${_link}`}
                      footerLink={footerLink}
                    />
                  );
                })}
              </ul>
            </li>
          ))}
          <li
            className="flex flex-col mobile:w-1/2 mobile:border-b mobile:border-light-grey mobile:pb-[30px] mobile:mb-[30px] mobile:pr-[15px]"
            key={`footer_item_${contentfulFooter.footeritem.length + 1}`}
          >
            <span className="flex items-center w-full whitespace-nowrap typo-body font-bold mb-[20px]">
              Contact Us
            </span>
            <ul className="flex flex-col list-none pl-0 gap-[15px]">
              {Object.keys(hoursCustomerSupport).length > 0 ? (
                <li className="whitespace-nowrap typo-micro text-black mobile:typo-body">
                  {dict.get(
                    customerSupport.sentence,
                    hoursCustomerSupport.start,
                    hoursCustomerSupport.end
                  )}
                </li>
              ) : null}
              <li className="whitespace-nowrap typo-micro text-black mobile:typo-body">
                <a
                  className="flex justify-start items-center border-0"
                  href="mailto:love@analuisa.com"
                >
                  <MessageIcon width="15" height="10" fill="none" stroke="#000000" />
                  <span className="ml-[10px]">Email</span>
                </a>
              </li>
              <li className="whitespace-nowrap typo-micro text-black mobile:typo-body">
                <button
                  className="flex justify-start items-center border-0 chat_icon"
                  onClick={() => openGorgias()}
                  type="button"
                >
                  <LiveChatIcon width="15" height="13" fill="none" />
                  <span className="ml-[10px]">Chat Now</span>
                </button>
              </li>
              <li className="whitespace-nowrap typo-micro text-black mobile:typo-body">
                <button
                  className="flex justify-start items-center border-0 chat_icon"
                  onClick={() => openGorgias()}
                  type="button"
                >
                  <LuxeChatSupport width="15" height="13" fill="none" />
                  <span className="ml-[10px]">AL Luxe Member Chat</span>
                </button>
              </li>
              <li className="typo-micro text-black mobile:typo-body">
                <a className="border-0" href="sms:+18446580027">
                  <div className="flex justify-start items-center">
                    <CallIcon width="12" height="15" fill="none" stroke="#000000" />
                    <span className="ml-[10px]">
                      +1 (844)-658-0027{isMobile ? <br /> : "\u00A0"}(Text only)
                    </span>
                  </div>
                </a>

                <p
                  className="cursor-pointer underline flex items-center mobile:items-start mt-[6px] ml-[22px] typo-micro mobile:typo-body font-bold"
                  onClick={() => setIsSmsTermsOpen((prev) => !prev)}
                  type="button"
                >
                  SMS Terms
                  <IconArrow
                    style={{
                      transform: isSmsTermsOpen ? "rotate(90deg)" : "rotate(270deg)",
                      marginLeft: "10px",
                      transition: "transform 300ms linear",
                    }}
                    width="7"
                    height="14"
                  />
                </p>
                {isSmsTermsOpen && (
                  <p className="typo-micro mobile:typo-body italic max-w-[392px] mt-[4px] ml-[22px]">
                    You can text us at +1 844-658-0027 for support. By texting this number, you
                    agree to receive SMS messages from Ana Luisa related to your inquiry. Message
                    frequency may vary. Msg & data rates may apply. Reply STOP to unsubscribe or
                    HELP for help.
                  </p>
                )}
              </li>
            </ul>
          </li>
        </ul>

        <div className="mobile:px-[15px] mobile:border-b mobile:border-t mobile:border-dark-grey mobile:border-b-light-grey mobile:pb-[30px] mobile:pt-[30px]">
          <CaptureEmail />
          <div className="flex gap-[25px] mt-[20px] mobile:gap-[20px]">
            {socialMediaElements.map((social) => (
              <a
                href={social.link}
                target="_blank"
                rel="noreferrer noopener"
                title={social.title}
                key={`footer_social_${social.title}`}
              >
                {social.image}
              </a>
            ))}
          </div>
        </div>
      </div>

      <hr className="border-t border-light-grey w-full mobile:hidden" />

      <div className="flex flex-row justify-between mx-auto px-[1.74vw] py-[20px] w-full mobile:flex-col mobile:gap-[20px] mobile:px-[15px] mobile:pb-[20px] mobile:pt-0 items-center mobile:items-start">
        <div className="flex flex-row gap-[30px] items-center mobile:flex-col mobile:items-start mobile:gap-0">
          {!isMobile && (
            <LocalesPopUp
              showCountry={false}
              showISO={false}
              componentType={ComponentType.FOOTER}
            />
          )}
          {isMobile && <p className="typo-h3 font-bold mb-[10px]">Secure Checkout Options</p>}
          <ALPaymentMethod locale={gePriceDetails.CountryCode} className="h-[24px] my-0" />
        </div>
        <div className="footer_legal_locale">
          {isMobile && <LocalesPopUp componentType={ComponentType.FOOTER} showCountry={false} />}

          <ul className="flex self-center mobile:flex-wrap mobile:pt-[10px]">
            <li className="[&:not(:last-child)::after]:content-['|'] [&:not(:last-child)::after]:inline-block [&:not(:last-child)::after]:mx-1 [&:not(:last-child)::after]:text-[#898786] mobile:leading-[14px]">
              <span className="typo-micro text-dark-grey">© Ana Luisa™ {getCurrentYear()}</span>
            </li>
            {sitemapElements.map((e) => {
              if (e.link.includes("https:")) {
                return (
                  <a
                    className="typo-micro text-dark-grey [&:not(:last-child)::after]:content-['|'] [&:not(:last-child)::after]:inline-block [&:not(:last-child)::after]:mx-1 [&:not(:last-child)::after]:text-[#898786] mobile:leading-[14px]"
                    href={`${e.link}`}
                    key={`footer_sitemap_${e.title}`}
                  >
                    {e.title}
                  </a>
                );
              }
              return (
                <li
                  className="[&:not(:last-child)::after]:content-['|'] [&:not(:last-child)::after]:inline-block [&:not(:last-child)::after]:mx-1 [&:not(:last-child)::after]:text-[#898786] mobile:leading-[14px]"
                  key={`footer_sitemap_${e.title}`}
                >
                  <Link className="typo-micro text-dark-grey" to={e.link}>
                    {e.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </footer>
  );
}

export default withALErrorBoundary({
  name: "Footer",
  priority: "P3",
})(Footer);
